import { ReactNode } from 'react';
import { Box, Button, CircularProgress, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { Paper } from '@shared/ui';
import { Connector } from '@domain/connection/models/Connector';
import ConnectionSetupHeader from './ConnectionSetupHeader';

export interface SetupStep {
  id: string;
  label: string;
}

interface SetupFrameProps {
  connector: Connector;
  steps: SetupStep[];
  step: string;
  busy: boolean;
  primaryLabel: string;
  primaryDisabled: boolean;
  onPrimary: () => void;
  onClose?: () => void;
  onStepChange?: (step: string) => void;
  error: string | null;
  children: ReactNode;
}

const SetupFrame = ({
  connector,
  steps,
  step,
  busy,
  primaryLabel,
  primaryDisabled,
  onPrimary,
  onClose,
  onStepChange,
  error,
  children,
}: SetupFrameProps) => {
  const theme = useTheme();
  const colors = theme.palette;
  const stepIndex = steps.findIndex(tab => tab.id === step);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Stack alignItems="center" p={4} pt={0}>

        <ConnectionSetupHeader connector={connector} />

        <Paper sx={{ width: '100%', maxWidth: 800, boxShadow: '0px 10px 20px 0px rgba(0, 0, 0, 0.05)', overflow: 'hidden' }}>

          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ px: 1, borderBottom: `1px solid ${colors.divider}` }}>

            {onClose && (
              <Box sx={{ flexGrow: 1, textAlign: 'left', py: 1 }}>
                <Button variant="text" color="inherit" onClick={onClose} disabled={busy}>Close</Button>
              </Box>
            )}

            {steps.length > 1 && (
              <Tabs value={step} onChange={(_, value: string) => onStepChange?.(value)}>
                {steps.map((item, index) => (
                  <Tab disabled={index > stepIndex} key={item.id} value={item.id} label={item.label} sx={{ textTransform: 'none' }} />
                ))}
              </Tabs>
            )}

            <Box sx={{ flexGrow: 1, py: 1, textAlign: 'right' }}>
              <Button variant="contained" onClick={onPrimary} disabled={primaryDisabled || busy}>
                {busy ? <CircularProgress size={16} color="inherit" /> : primaryLabel}
              </Button>
            </Box>

          </Stack>

          {error && <Typography color="error" variant="body2">{error}</Typography>}

          {children}

        </Paper>
      </Stack>
    </Box>
  );
};

export default SetupFrame;
